import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { loginStart, loginSuccess, loginFailure } from '../../store/authSlice';
import { showToast } from '../../store/uiSlice';
import api from '../../services/api';

const SignIn = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [focusedField, setFocusedField] = useState(null);

  useEffect(() => {
    localStorage.removeItem('__admin_backup_user');
    localStorage.removeItem('__admin_backup_token');
    localStorage.removeItem('__is_impersonating');
    localStorage.removeItem('__admin_token_backup');
    localStorage.removeItem('__admin_refresh_backup');
    localStorage.removeItem('__admin_user_backup');
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email || !password) {
      dispatch(showToast({ message: 'Please fill in all fields.', type: 'error' }));
      return;
    }

    dispatch(loginStart());

    try {
      const response = await api.post('/auth/login', { email, password, rememberMe });
      dispatch(loginSuccess({ user: response.data.user, token: response.data.token }));
      dispatch(showToast({ message: 'Login successful! Redirecting to terminal...', type: 'success' }));
      setTimeout(() => navigate('/terminal'), 1800);
    } catch (error) {
      const message = error.response?.data?.message || 'Login failed. Please try again.';
      dispatch(loginFailure(message));
      dispatch(showToast({ message, type: 'error' }));
    }
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-3 xs:p-4 sm:p-6">
      {/* Header Navigation — Responsive */}
      <div className="h-[5rem] absolute top-0 left-0 right-0 xs:h-20 flex items-center justify-between px-3 xs:px-6 sm:px-8 py-3 xs:py-0  border-[#e2e8f0] min-h-[70px] xs:min-h-[80px]">
        {/* Logo — Responsive & Big */}
        <Link to="/" className="flex items-center gap-2 flex-shrink-0">
          <img 
            src="/src/assets/img/logo/logo-white.svg" 
            alt="Anzo Trade" 
            className="object-contain transition-transform duration-300 hover:scale-105"
            style={{ 
              width: 'clamp(120px, 10vw, 150px)',
              height: 'clamp(140px, 7.5vw, 112px)'
            }}
          />
        </Link>

        {/* Register Link — Responsive Text */}
        <Link to="/register" className="text-[0.65rem] xs:text-xs sm:text-sm tracking-wider font-bold text-[#64748b] hover:text-[#8b5cf6] transition-colors uppercase whitespace-nowrap flex-shrink-0 ml-4">
          Register
        </Link>
      </div>

      {/* Main Container */}
      <div className="w-full max-w-md">
        <div className="mt-[90px] xs:mt-24 sm:mt-28 md:mt-32">
          {/* Title Section */}
          <div className="mb-8 xs:mb-10 sm:mb-12">
            <h1 className="text-3xl xs:text-4xl sm:text-5xl font-bold text-[#0f172a] mb-[1.5rem] xs:mb-3 tracking-tight">
              Sign In
            </h1>
            <p className="text-[0.7rem] xs:text-xs sm:text-xs tracking-wider font-bold text-[#8b5cf6] uppercase">
              Welcome back to trading
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5 xs:space-y-6 sm:space-y-7">
            {/* Email Input */}
            <div>
              <label className="block text-[10px] xs:text-[11px] sm:text-xs tracking-wider font-bold text-[#5b21b6] uppercase mb-1.5 xs:mb-2">
                Email
              </label>
              <div className="relative">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onFocus={() => setFocusedField('email')}
                  onBlur={() => setFocusedField(null)}
                  placeholder="you@example.com"
                  className={`w-full px-3 py-2.5 xs:py-3 sm:py-3.5 text-sm xs:text-base text-[#0f172a] font-medium bg-transparent border-b-[3px] outline-none transition-all duration-300 placeholder:text-[#cbd5e1] ${
                    focusedField === 'email'
                      ? 'border-b-[#8b5cf6]'
                      : 'border-b-[#cbd5e1] hover:border-b-[#a78bfa]'
                  }`}
                />
              </div>
            </div>

            {/* Password Input */}
            <div>
              <label className="block text-[10px] xs:text-[11px] sm:text-xs tracking-wider font-bold text-[#5b21b6] uppercase mb-1.5 xs:mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onFocus={() => setFocusedField('password')}
                  onBlur={() => setFocusedField(null)}
                  placeholder="••••••••"
                  className={`w-full px-3 py-2.5 xs:py-3 sm:py-3.5 pr-10 text-sm xs:text-base text-[#0f172a] font-medium bg-transparent border-b-[3px] outline-none transition-all duration-300 placeholder:text-[#cbd5e1] ${
                    focusedField === 'password'
                      ? 'border-b-[#8b5cf6]'
                      : 'border-b-[#cbd5e1] hover:border-b-[#a78bfa]'
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-0 top-1/2 -translate-y-1/2 text-[#94a3b8] hover:text-[#8b5cf6] transition-colors p-2"
                >
                  <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'} text-xs xs:text-sm`} />
                </button>
              </div>
            </div>

            {/* Remember Me */}
            <div className="flex items-center justify-between pt-1 xs:pt-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={e => setRememberMe(e.target.checked)}
                  className="w-4 h-4 rounded accent-[#8b5cf6]"
                />
                <span className="text-xs xs:text-sm font-medium text-[#475569]">Remember me</span>
              </label>
              <Link to="/forgot-password" className="text-xs font-bold text-[#8b5cf6] hover:text-[#7c3aed] transition-colors">
                Forgot password?
              </Link>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              className="w-full py-3 xs:py-3.5 sm:py-4 px-4 xs:px-5 sm:px-6 mt-6 xs:mt-7 sm:mt-8 font-bold text-xs xs:text-sm sm:text-base uppercase tracking-wider text-white rounded-md hover:shadow-xl hover:shadow-[#a834cb]/30 active:scale-[0.98] transition-all duration-300 border-b-[2px] xs:border-b-[3px] border-[#8b2db5]"
              style={{ background: 'linear-gradient(to right, #a834cb, #8b5cf6, #6366f1)' }}
            >
              Sign In
            </button>
          </form>

          {/* Divider */}
          <div className="mt-8 xs:mt-9 sm:mt-10 mb-5 xs:mb-6 flex items-center gap-4">
            <div className="flex-1 h-px bg-[#e2e8f0]" />
            <span className="text-[0.7rem] xs:text-xs text-[#8b5cf6] font-bold">Or</span>
            <div className="flex-1 h-px bg-[#e2e8f0]" />
          </div>

          {/* Register Link */}
          <div className="text-center">
            <p className="text-[0.75rem] xs:text-xs sm:text-sm text-[#475569] font-medium tracking-wide">
              Don't have an account?{' '}
              <Link to="/register" className="text-[#8b5cf6] font-bold hover:text-[#7c3aed] transition-colors">
                Create account
              </Link>
            </p>
          </div>
        </div>
      </div>

      <style>{`
        input::placeholder {
          font-weight: 400;
        }
        
        button:active {
          transform: scale(0.98);
        }
      `}</style>
    </div>
  );
};

export default SignIn;